import React, { useState, useEffect, useMemo, useRef } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  FiArrowLeft, FiSave, FiLock, FiCalendar, FiMapPin, FiClock, FiBookOpen,
  FiCamera, FiUpload, FiTrash2, FiImage, FiMaximize2, FiX
} from 'react-icons/fi';
import { toast } from 'react-toastify';
import { reuniaoJovensService, bibliaService } from '../../services/api';
import { useAuth } from '../../contexts/AuthContext';
import './ReuniaoJovens.css';

const CATEGORIAS = [
  { id: 'MENINAS',  label: 'Meninas',  cor: '#ec4899', recita: true  },
  { id: 'MOCAS',    label: 'Moças',    cor: '#a855f7', recita: true  },
  { id: 'MENINOS',  label: 'Meninos',  cor: '#3b82f6', recita: true  },
  { id: 'MOCOS',    label: 'Moços',    cor: '#0ea5e9', recita: true  },
  { id: 'CASADOS',  label: 'Casados',  cor: '#f59e0b', recita: false },
];

const ReuniaoJovensDetalhes = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const { canWriteModule } = useAuth();
  const podeEditar = canWriteModule('reuniao_jovens');

  const [reuniao, setReuniao] = useState(null);
  const [loading, setLoading] = useState(true);
  const [salvando, setSalvando] = useState(false);
  const [livros, setLivros] = useState([]);

  const [dados, setDados] = useState({});
  const [palavra, setPalavra] = useState({
    livro: '', capitulo: '', versoInicio: '', versoFim: '',
  });
  const [observacoes, setObservacoes] = useState('');

  // Foto da contagem
  const [fotoUrl, setFotoUrl] = useState(null);
  const [enviandoFoto, setEnviandoFoto] = useState(false);
  const [fotoAmpliada, setFotoAmpliada] = useState(false);
  const inputFotoRef = useRef(null);
  const inputCameraRef = useRef(null);

  const carregar = async () => {
    try {
      setLoading(true);
      const resp = await reuniaoJovensService.getById(id);
      const r = resp.data;
      setReuniao(r);

      const inicial = {};
      (r.relatorios || []).forEach((it) => {
        inicial[it.categoria] = {
          compareceram: it.compareceram || 0,
          recitaram: it.recitaram || 0,
        };
      });
      CATEGORIAS.forEach((c) => {
        if (!inicial[c.id]) inicial[c.id] = { compareceram: 0, recitaram: 0 };
      });
      setDados(inicial);

      setPalavra({
        livro: r.livro || '',
        capitulo: r.capitulo || '',
        versoInicio: r.versoInicio || '',
        versoFim: r.versoFim || '',
      });
      setObservacoes(r.observacoes || '');
      setFotoUrl(r.fotoUrl || null);
    } catch {
      toast.error('Erro ao carregar reunião');
      navigate('/reuniao-jovens');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    carregar();
    bibliaService.listarLivros().then((r) => setLivros(r.data || [])).catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  const livroAtual = useMemo(
    () => livros.find((l) => l.nome === palavra.livro),
    [livros, palavra.livro]
  );
  const maxCap = livroAtual?.capitulos || null;
  const maxVerso = livroAtual && palavra.capitulo
    ? livroAtual.versosPorCapitulo[parseInt(palavra.capitulo, 10) - 1] || null
    : null;

  const CATS_JOVENS = ['MENINAS', 'MOCAS', 'MENINOS', 'MOCOS'];

  const totalJovens = useMemo(
    () => CATS_JOVENS.reduce((acc, cat) => acc + (parseInt(dados[cat]?.compareceram, 10) || 0), 0),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [dados]
  );
  const totalJovensRec = useMemo(
    () => CATS_JOVENS.reduce((acc, cat) => acc + (parseInt(dados[cat]?.recitaram, 10) || 0), 0),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [dados]
  );
  const totalGeral = useMemo(
    () => Object.values(dados).reduce((acc, d) => acc + (parseInt(d.compareceram, 10) || 0), 0),
    [dados]
  );
  const totalRecitaram = useMemo(
    () => CATS_JOVENS.reduce((acc, cat) => acc + (parseInt(dados[cat]?.recitaram, 10) || 0), 0),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [dados]
  );

  const setValor = (categoria, campo, valor) => {
    setDados((prev) => ({
      ...prev,
      [categoria]: { ...prev[categoria], [campo]: valor },
    }));
  };

  const salvar = async () => {
    try {
      setSalvando(true);
      const categorias = CATEGORIAS.map((c) => ({
        categoria: c.id,
        compareceram: parseInt(dados[c.id]?.compareceram, 10) || 0,
        recitaram: c.recita ? (parseInt(dados[c.id]?.recitaram, 10) || 0) : 0,
      }));

      await reuniaoJovensService.salvarRelatorio(id, {
        categorias,
        livro: palavra.livro || null,
        capitulo: palavra.capitulo ? parseInt(palavra.capitulo, 10) : null,
        versoInicio: palavra.versoInicio ? parseInt(palavra.versoInicio, 10) : null,
        versoFim: palavra.versoFim ? parseInt(palavra.versoFim, 10) : null,
        observacoes,
      });

      toast.success('Relatório salvo!');
      carregar();
    } catch {
      toast.error('Erro ao salvar relatório');
    } finally {
      setSalvando(false);
    }
  };

  const handleFotoChange = async (e) => {
    const arquivo = e.target.files?.[0];
    if (!arquivo) return;
    if (arquivo.size > 25 * 1024 * 1024) {
      toast.error('Imagem muito grande (máx 25 MB)');
      return;
    }
    try {
      setEnviandoFoto(true);
      // Preview imediato
      const previewUrl = URL.createObjectURL(arquivo);
      setFotoUrl(previewUrl);
      // Comprime no celular (iPhone) antes de enviar — evita timeout / form cortado
      const { comprimirImagem } = await import('../../utils/comprimirImagem');
      const paraEnviar = await comprimirImagem(arquivo);
      const resp = await reuniaoJovensService.uploadFoto(id, paraEnviar);
      setFotoUrl(resp.fotoUrl);
      toast.success('Foto salva!');
    } catch (err) {
      const msg = err?.response?.data?.error || err?.message || 'Erro ao enviar foto';
      toast.error(msg);
      setFotoUrl(null);
    } finally {
      setEnviandoFoto(false);
      // Limpa o input para permitir reenvio do mesmo arquivo
      if (e.target) e.target.value = '';
    }
  };

  const handleRemoverFoto = async () => {
    if (!window.confirm('Remover a foto desta reunião?')) return;
    try {
      await reuniaoJovensService.removerFoto(id);
      setFotoUrl(null);
      toast.success('Foto removida');
    } catch {
      toast.error('Erro ao remover foto');
    }
  };

  const avancarCampo = (e) => {
    if (e.key !== 'Enter') return;
    e.preventDefault();
    const inputs = Array.from(
      e.target.closest('table').querySelectorAll('input.rj-num:not(:disabled)')
    );
    const idx = inputs.indexOf(e.target);
    if (idx >= 0 && idx < inputs.length - 1) inputs[idx + 1].focus();
  };

  const fechar = async () => {
    if (!window.confirm('Após fechar, a reunião não poderá mais ser editada. Confirma?')) return;
    try {
      await reuniaoJovensService.fechar(id);
      toast.success('Reunião fechada com sucesso');
      carregar();
    } catch {
      toast.error('Erro ao fechar reunião');
    }
  };

  if (loading) return <div className="rj-page"><div className="rj-empty">Carregando…</div></div>;
  if (!reuniao) return null;

  const bloqueado = reuniao.fechado || !podeEditar;

  return (
    <div className="rj-page rj-detalhes">
      <button className="rj-back" onClick={() => navigate('/reuniao-jovens')}>
        <FiArrowLeft /> Voltar
      </button>

      <header className="rj-page-header">
        <div>
          <h1>Relatório da Reunião</h1>
          <div className="rj-meta-info">
            <span><FiCalendar /> {(() => { const s = String(reuniao.dataReuniao).substring(0,10); const [a,m,d] = s.split('-').map(Number); return new Date(a,m-1,d).toLocaleDateString('pt-BR'); })()}</span>
            <span><FiClock /> {reuniao.horario || '10:00'}</span>
            <span><FiMapPin /> {reuniao.congregacaoNome}</span>
          </div>
        </div>
        {reuniao.fechado && (
          <span className="rj-tag rj-tag-fechada"><FiLock size={12} /> Fechada</span>
        )}
      </header>

      {/* Aviso de bloqueio */}
      {reuniao.fechado && (
        <div className="rj-aviso-bloqueio">
          Esta reunião está fechada e não pode mais ser editada.
        </div>
      )}

      {/* Tabela do relatório */}
      <section className="rj-section">
        <h2>Comparecimento por categoria</h2>
        <p className="rj-section-sub">
          Os campos seguem exatamente o relatório oficial: Compareceram e Recitaram para cada categoria.
        </p>

        <div className="rj-tabela-wrap">
          <table className="rj-tabela">
            <thead>
              <tr>
                <th></th>
                <th>Compareceram</th>
                <th>Recitaram</th>
              </tr>
            </thead>
            <tbody>
              {CATEGORIAS.map((c) => (
                <tr key={c.id}>
                  <td>
                    <span className="rj-cat-pill" style={{ background: c.cor }}>
                      {c.label}
                    </span>
                  </td>
                  <td>
                    <input
                      type="number"
                      min={0}
                      value={dados[c.id]?.compareceram ?? 0}
                      onChange={(e) => setValor(c.id, 'compareceram', e.target.value)}
                      onFocus={(e) => e.target.select()}
                      onKeyDown={avancarCampo}
                      disabled={bloqueado}
                      className="rj-num"
                    />
                  </td>
                  <td>
                    {c.recita ? (
                      <input
                        type="number"
                        min={0}
                        max={parseInt(dados[c.id]?.compareceram, 10) || undefined}
                        value={dados[c.id]?.recitaram ?? 0}
                        onChange={(e) => setValor(c.id, 'recitaram', e.target.value)}
                        onFocus={(e) => e.target.select()}
                        onKeyDown={avancarCampo}
                        disabled={bloqueado}
                        className="rj-num"
                      />
                    ) : (
                      <span className="rj-muted">—</span>
                    )}
                  </td>
                </tr>
              ))}
              <tr className="rj-tabela-subtotal">
                <td>
                  <span className="rj-subtotal-label">Total de Jovens</span>
                  <span className="rj-subtotal-hint">Meninas + Moças + Meninos + Moços</span>
                </td>
                <td><strong>{totalJovens}</strong></td>
                <td><strong>{totalJovensRec}</strong></td>
              </tr>
              <tr className="rj-tabela-total">
                <td>
                  <strong>TOTAL GERAL</strong>
                  <span className="rj-subtotal-hint">Inclui Casados</span>
                </td>
                <td><strong>{totalGeral}</strong></td>
                <td><strong>{totalRecitaram}</strong></td>
              </tr>
            </tbody>
          </table>
        </div>
      </section>

      {/* Palavra (texto bíblico do dia) */}
      <section className="rj-section">
        <h2><FiBookOpen /> Palavra do dia</h2>
        <p className="rj-section-sub">
          O versículo lido na reunião (referência usada para os recitativos).
        </p>

        <div className="rj-form-grid">
          <label>
            <span>Livro</span>
            <select
              value={palavra.livro}
              onChange={(e) => setPalavra({ ...palavra, livro: e.target.value, capitulo: '', versoInicio: '', versoFim: '' })}
              disabled={bloqueado}
            >
              <option value="">Selecione…</option>
              {livros.map((l) => <option key={l.nome} value={l.nome}>{l.nome}</option>)}
            </select>
          </label>

          <label>
            <span>Capítulo {maxCap ? `(1–${maxCap})` : ''}</span>
            <input
              type="number"
              min={1}
              max={maxCap || undefined}
              value={palavra.capitulo}
              onChange={(e) => setPalavra({ ...palavra, capitulo: e.target.value, versoInicio: '', versoFim: '' })}
              disabled={bloqueado || !palavra.livro}
            />
          </label>

          <label>
            <span>Do verso {maxVerso ? `(máx ${maxVerso})` : ''}</span>
            <input
              type="number"
              min={1}
              max={maxVerso || undefined}
              value={palavra.versoInicio}
              onChange={(e) => setPalavra({ ...palavra, versoInicio: e.target.value })}
              disabled={bloqueado || !palavra.capitulo}
            />
          </label>

          <label>
            <span>Ao verso {maxVerso ? `(máx ${maxVerso})` : ''}</span>
            <input
              type="number"
              min={palavra.versoInicio || 1}
              max={maxVerso || undefined}
              value={palavra.versoFim}
              onChange={(e) => setPalavra({ ...palavra, versoFim: e.target.value })}
              disabled={bloqueado || !palavra.capitulo}
            />
          </label>
        </div>

        <label className="rj-form-full">
          <span>Observações</span>
          <textarea
            value={observacoes}
            onChange={(e) => setObservacoes(e.target.value)}
            rows={3}
            disabled={bloqueado}
            placeholder="Ex: visita do ministério, evento especial, etc."
          />
        </label>
      </section>

      {/* ── Foto da contagem ── */}
      <section className="rj-section">
        <h2><FiImage /> Foto da contagem</h2>
        <p className="rj-section-sub">
          Opcional — registre uma foto da lista de chamada ou da contagem realizada na reunião.
        </p>

        {fotoUrl ? (
          <div className="rj-foto-wrap">
            <div className="rj-foto-preview" onClick={() => setFotoAmpliada(true)}>
              <img
                src={fotoUrl.startsWith('blob:') ? fotoUrl : `https://sistemareuniao.online${fotoUrl}`}
                alt="Foto da contagem"
                className="rj-foto-img"
              />
              <div className="rj-foto-overlay"><FiMaximize2 /></div>
            </div>
            {!bloqueado && (
              <div className="rj-foto-acoes">
                <button
                  className="rj-btn rj-btn-ghost"
                  onClick={() => inputFotoRef.current?.click()}
                  disabled={enviandoFoto}
                >
                  <FiUpload /> Trocar foto
                </button>
                <button
                  className="rj-btn rj-btn-ghost rj-btn-danger-ghost"
                  onClick={handleRemoverFoto}
                  disabled={enviandoFoto}
                >
                  <FiTrash2 /> Remover
                </button>
              </div>
            )}
          </div>
        ) : (
          !bloqueado && (
            <div className="rj-foto-vazio">
              <FiImage className="rj-foto-vazio-icon" />
              <p>Nenhuma foto anexada</p>
              <div className="rj-foto-botoes">
                <button
                  className="rj-btn rj-btn-ghost"
                  onClick={() => inputCameraRef.current?.click()}
                  disabled={enviandoFoto}
                >
                  <FiCamera /> Tirar foto
                </button>
                <button
                  className="rj-btn rj-btn-ghost"
                  onClick={() => inputFotoRef.current?.click()}
                  disabled={enviandoFoto}
                >
                  <FiUpload /> {enviandoFoto ? 'Enviando…' : 'Escolher da galeria'}
                </button>
              </div>
            </div>
          )
        )}

        {/* Inputs ocultos */}
        <input
          ref={inputCameraRef}
          type="file"
          accept="image/*"
          capture="environment"
          style={{ display: 'none' }}
          onChange={handleFotoChange}
        />
        <input
          ref={inputFotoRef}
          type="file"
          accept="image/*"
          style={{ display: 'none' }}
          onChange={handleFotoChange}
        />
      </section>

      {/* Modal de foto ampliada */}
      {fotoAmpliada && fotoUrl && (
        <div className="rj-foto-modal" onClick={() => setFotoAmpliada(false)}>
          <button className="rj-foto-modal-fechar" onClick={() => setFotoAmpliada(false)}>
            <FiX />
          </button>
          <img
            src={fotoUrl.startsWith('blob:') ? fotoUrl : `https://sistemareuniao.online${fotoUrl}`}
            alt="Foto ampliada"
            className="rj-foto-modal-img"
            onClick={(e) => e.stopPropagation()}
          />
        </div>
      )}

      {/* Ações */}
      {!bloqueado && (
        <div className="rj-action-bar">
          <button
            className="rj-btn rj-btn-primary"
            onClick={salvar}
            disabled={salvando}
          >
            <FiSave /> {salvando ? 'Salvando…' : 'Salvar relatório'}
          </button>
          {(reuniao.totalGeral || 0) > 0 && (
            <button
              className="rj-btn rj-btn-ghost"
              onClick={fechar}
            >
              <FiLock /> Fechar reunião
            </button>
          )}
        </div>
      )}
    </div>
  );
};

export default ReuniaoJovensDetalhes;
